import {
  Badge,
  Button,
  Card,
  Flex,
  Progress,
  Text,
  Tooltip,
} from '@retracedgmbh/ui-components-lib';
import classNames from 'classnames';
import useTranslator from '@/hooks/useTranslator';
import type { AuditStage, AuditStageId } from './auditStages';

// Static stage-id → i18n key map (never a dynamic `stage.${id}` lookup, per the
// no-dynamic-translation-keys convention).
const STAGE_LABEL_KEYS: Record<AuditStageId, string> = {
  auditDetails: 'stage.auditDetails',
  capExtraction: 'stage.capExtraction',
  findings: 'stage.findings',
};

// Why a REVIEW row's Review button is disabled:
//  - `auditDetailsFirst`  → chronology: the extracted audit details must be
//    reviewed before any other review job on the same card;
//  - `capImportContext`   → a CAP-only import whose queue row never recorded the
//    audit + company it belongs to, so there is no review to open.
export type ReviewDisabledReason = 'auditDetailsFirst' | 'capImportContext';

// Static reason → i18n key map (never a dynamic lookup, per the
// no-dynamic-translation-keys convention).
const REVIEW_DISABLED_TOOLTIP_KEYS: Record<ReviewDisabledReason, string> = {
  auditDetailsFirst: 'stage.auditDetailsFirstTooltip',
  capImportContext: 'stage.capImportContextTooltip',
};

interface StageRowProps {
  stage: AuditStage;
  onReview: () => void;
  // REVIEW: gate the Review button — disabled + a tooltip explaining why.
  // `undefined` = not gated (the normal case).
  reviewDisabledReason?: ReviewDisabledReason;
  errorMessage?: string; // FAILED: message from the backend; falls back to i18n
  canRetry?: boolean; // FAILED: show the Retry button (audit only)
  onRetry?: () => void; // FAILED: retry handler
  onDismiss?: () => void; // FAILED: the error-card X (dismiss the card)
  isRetrying?: boolean; // FAILED: Retry button loading state
}

export default function StageRow({
  stage,
  onReview,
  reviewDisabledReason,
  errorMessage,
  canRetry,
  onRetry,
  onDismiss,
  isRetrying,
}: StageRowProps) {
  const { getTranslation } = useTranslator({ page: 'ai-processing' });
  const label = getTranslation(STAGE_LABEL_KEYS[stage.id]);

  if (stage.status === 'FAILED') {
    return (
      <Card
        variant='outline'
        className='tw:p-md tw:bg-background-negative-subtle tw:border-border-negative'
      >
        <Flex direction='column' gap='sm'>
          <Flex justify='between' alignItems='center' gap='sm'>
            <Flex alignItems='center' gap='sm' className='tw:min-w-0'>
              <i className='fa-regular fa-triangle-exclamation tw:text-content-negative tw:shrink-0' />
              <Text size='small' weight='semibold' textOverFlow='truncate'>
                {label}
              </Text>
            </Flex>
            <Flex alignItems='center' gap='sm' className='tw:shrink-0'>
              <Text
                size='small'
                className='tw:text-content-negative tw:whitespace-nowrap'
              >
                {getTranslation('itemCard.status.failed')}
              </Text>
              {canRetry ? (
                <Button
                  variant='ghost'
                  size='sm'
                  icon='fa-regular fa-arrows-rotate'
                  iconPosition='left'
                  onClick={onRetry}
                  loading={isRetrying}
                >
                  {getTranslation('stage.retry')}
                </Button>
              ) : null}
              <Button variant='ghost' size='icon' onClick={onDismiss}>
                <i className='fa-regular fa-xmark' />
              </Button>
            </Flex>
          </Flex>
          <Text as='p' size='small' className='tw:text-content-negative'>
            {errorMessage || getTranslation('stage.errorMessage')}
          </Text>
        </Flex>
      </Card>
    );
  }

  if (stage.status === 'PROCESSING') {
    return (
      <Card variant='outline' className='tw:p-md'>
        <Flex direction='column' gap='sm'>
          <Flex justify='between' alignItems='center' gap='sm'>
            <Flex alignItems='center' gap='sm' className='tw:min-w-0'>
              <i className='fa-regular fa-circle tw:text-content-secondary' />
              <Text size='small' weight='semibold' textOverFlow='truncate'>
                {label}
              </Text>
            </Flex>
            <Flex alignItems='center' gap='sm'>
              <Text
                size='small'
                weight='semibold'
                className='tw:text-content-brand tw:whitespace-nowrap'
              >
                {getTranslation('stage.percent', { percentage: stage.percent })}
              </Text>
            </Flex>
          </Flex>
          <Progress className='tw:h-2' value={stage.percent} />
        </Flex>
      </Card>
    );
  }

  const isReview = stage.status === 'REVIEW';

  return (
    <Card
      variant='outline'
      className={classNames(
        'tw:p-md',
        isReview
          ? 'tw:bg-background-notice-primary-subtle'
          : 'tw:bg-background-secondary',
      )}
    >
      <Flex justify='between' alignItems='center'>
        <Flex alignItems='center' gap='sm'>
          <i
            className={classNames(
              'fa-regular fa-circle-check',
              isReview
                ? 'tw:text-content-notice-primary'
                : 'tw:text-content-brand',
            )}
          />
          <Text size='small' weight='semibold'>
            {label}
          </Text>
        </Flex>
        {isReview ? (
          reviewDisabledReason ? (
            // Gated (chronology, or a CAP import with no recorded context). A
            // disabled button swallows pointer events, so the tooltip wraps a
            // span trigger instead of the button itself.
            <Tooltip
              title={getTranslation(
                REVIEW_DISABLED_TOOLTIP_KEYS[reviewDisabledReason],
              )}
              placement='top'
            >
              <span className='tw:inline-flex'>
                <Button
                  disabled
                  icon='fa-regular fa-chevron-right'
                  iconPosition='right'
                >
                  {getTranslation('itemCard.review')}
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Button
              onClick={onReview}
              icon='fa-regular fa-chevron-right'
              iconPosition='right'
            >
              {getTranslation('itemCard.review')}
            </Button>
          )
        ) : (
          <Badge radius='squared' type='primary'>
            {getTranslation('itemCard.status.completed')}
          </Badge>
        )}
      </Flex>
    </Card>
  );
}
